import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import {getCoreRowModel, getExpandedRowModel, RowData, useReactTable} from "@tanstack/react-table";
import {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useReactToPrint} from "react-to-print";
import {
    isServerTableRowsCustomValid,
    ServerTableRowsMode,
    ServerTableRowsPicker,
    serverTableRowsCustomMax,
    ServerTableRowsValue
} from "../rows/ServerTableRowsPicker";
import {ServerTableAllRows, ServerTableInstance} from "../types";
import {ServerTablePrintColumnPicker} from "./ServerTablePrintColumnPicker";
import {ServerTablePrintColumnRibbon} from "./ServerTablePrintColumnRibbon";
import {ServerTablePrintPreview} from "./ServerTablePrintPreview";
import {MIN_PRINT_COLUMN_WIDTH, PRINT_TEXT_SIZES} from "./constants";
import {
    ServerTablePrintColumn,
    ServerTablePrintOrientation,
    ServerTablePrintRenderMode,
    ServerTablePrintRows,
    ServerTablePrintTextSize
} from "./types";
import {
    clearServerTablePrintConfig,
    getDefaultServerTablePrintColumnIds,
    getPrintContentWidth,
    getServerTablePrintColumns,
    hasPrintColumnBelowMinimumWidth,
    readServerTablePrintConfig,
    writeServerTablePrintConfig
} from "./utils";

const EMPTY_ROWS: never[] = [];

/** Custom's starting count, under the list's total. */
const DEFAULT_CUSTOM_PRINT_ROWS = 100;

/** Typing a count waits this long before collecting, so each keystroke does not start a collection. */
const CUSTOM_ROWS_DEBOUNCE_MS = 400;

interface ServerTablePrintDialogProps<TData extends RowData> {
    open: boolean
    onClose: () => void
    table: ServerTableInstance<TData>
    title?: string
    selectedDate?: Date
    initialOrientation?: ServerTablePrintOrientation
    /** Collects rows beyond the loaded page; without it the print is the loaded page. */
    printRows?: ServerTablePrintRows<TData>
}

export function ServerTablePrintDialog<TData extends RowData>({
    open,
    onClose,
    table,
    title,
    selectedDate,
    initialOrientation = 'landscape',
    printRows
}: ServerTablePrintDialogProps<TData>) {
    const tableId = table.options.meta.tableId;
    const {t, i18n} = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language;
    const fullScreen = useMediaQuery('(max-width:900px)');
    const documentRef = useRef<HTMLDivElement>(null);

    // Selection order is also the printed column order.
    const [orientation, setOrientation] = useState<ServerTablePrintOrientation>(initialOrientation);
    const [textSize, setTextSize] = useState<ServerTablePrintTextSize>('standard');
    const [renderMode, setRenderMode] = useState<ServerTablePrintRenderMode>('raw');
    const [selectedColumnIds, setSelectedColumnIds] = useState<string[]>([]);
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
    const [pageCount, setPageCount] = useState<number | null>(null);
    const [oversizedRowCount, setOversizedRowCount] = useState(0);
    const [allRows, setAllRows] = useState<ServerTableAllRows<TData> | null>(null);
    const [allRowsLoading, setAllRowsLoading] = useState(false);
    const [allRowsFailed, setAllRowsFailed] = useState(false);
    const [rowsChoice, setRowsChoice] = useState<ServerTableRowsValue>({mode: 'page', custom: null});
    const [rowsChoiceOpen, setRowsChoiceOpen] = useState(false);
    const lastRequestModeRef = useRef<ServerTableRowsMode | null>(null);
    const initializedTableIdRef = useRef<string | null>(null);
    const initialColumnLayoutRef = useRef<{
        columnIds: string[]
        columnWidths: Record<string, number>
    } | null>(null);

    const leafColumns = table.getAllLeafColumns();
    const liveColumnSizing = table.getState().columnSizing;
    const columns = useMemo(
        () => getServerTablePrintColumns(leafColumns, liveColumnSizing, t),
        [leafColumns, liveColumnSizing, t]
    );
    const defaultColumnIds = useMemo(() => getDefaultServerTablePrintColumnIds(columns), [columns]);

    // Initialize once per open session so table refreshes cannot replace in-progress layout edits.
    useEffect(() => {
        if (!open) {
            initializedTableIdRef.current = null;
            return;
        }
        if (initializedTableIdRef.current === tableId) return;

        const persisted = readServerTablePrintConfig(tableId);
        const availableIds = new Set(columns.map(column => column.id));
        const storedIds = persisted?.columnIds.filter(id => availableIds.has(id)) ?? [];
        const initialColumnIds = storedIds.length ? storedIds : defaultColumnIds;
        const initialColumnWidths = persisted?.columnWidths ?? {};

        setOrientation(persisted?.orientation ?? initialOrientation);
        setTextSize(persisted?.textSize ?? 'standard');
        setRenderMode(persisted?.renderMode ?? 'raw');
        setSelectedColumnIds(initialColumnIds);
        setColumnWidths(initialColumnWidths);
        initialColumnLayoutRef.current = {
            columnIds: [...initialColumnIds],
            columnWidths: {...initialColumnWidths}
        };
        initializedTableIdRef.current = tableId;
    }, [columns, defaultColumnIds, initialOrientation, open, tableId]);

    const pageRowCount = table.getCoreRowModel().rows.length;
    const totalRowCount = table.getRowCount();
    const printMaxRows = printRows?.maxRows;
    const customMax = serverTableRowsCustomMax(totalRowCount, printMaxRows);

    // The rows choice is not persisted: every opening starts on the list's default.
    if (open !== rowsChoiceOpen) {
        setRowsChoiceOpen(open);
        if (open) {
            const custom = Math.max(1, Math.min(DEFAULT_CUSTOM_PRINT_ROWS, customMax));
            const overCap = printMaxRows != null && totalRowCount > printMaxRows;
            setRowsChoice(!printRows?.defaultAll
                ? {mode: 'page', custom}
                : overCap
                    ? {mode: 'custom', custom: printMaxRows}
                    : {mode: 'all', custom});
        }
    }

    const customValid = isServerTableRowsCustomValid(rowsChoice.custom);
    const collectsRows = !!printRows && open && rowsChoice.mode !== 'page';
    // `null` waits for a valid count; `undefined` collects every row under the cap.
    const requestLimit: number | null | undefined = rowsChoice.mode === 'all'
        ? undefined
        : customValid ? Math.min(rowsChoice.custom as number, customMax) : null;
    const requestKey = collectsRows ? `${rowsChoice.mode}:${requestLimit}` : null;

    /*
        The caller's queryFn is usually an inline closure, so the collector can change identity on
        every render. Opening the dialog and the rows choice trigger the collection, not that identity.
        A typed count waits for the typing to settle; picking a card collects at once.
     */
    useEffect(() => {
        if (!collectsRows || !printRows) {
            lastRequestModeRef.current = null;
            setAllRows(null);
            setAllRowsFailed(false);
            setAllRowsLoading(false);
            return;
        }
        if (requestLimit === null) {
            setAllRowsLoading(false);
            return;
        }

        const debounce = rowsChoice.mode === 'custom' && lastRequestModeRef.current === 'custom';
        lastRequestModeRef.current = rowsChoice.mode;
        let cancelled = false;
        setAllRowsFailed(false);
        setAllRowsLoading(true);

        const collect = () => printRows.fetch(requestLimit === undefined ? undefined : {maxRows: requestLimit})
            .then(result => {
                if (!cancelled) setAllRows(result);
            })
            .catch(error => {
                console.log(error);
                if (cancelled) return;
                setAllRows(null);
                setAllRowsFailed(true);
            })
            .finally(() => {
                if (!cancelled) setAllRowsLoading(false);
            });
        const timer = debounce ? setTimeout(collect, CUSTOM_ROWS_DEBOUNCE_MS) : undefined;
        if (!debounce) void collect();

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);

    /*
        Print columns render from TanStack rows, so the collected result set needs a row model of
        its own. It shares the live table's column definitions, row ids, and meta. Grouped tables
        print every child row: the full list is meant to name everyone, not only the groups that
        happen to be expanded on screen.
     */
    const allRowsTable = useReactTable<TData>({
        data: allRows?.rows ?? EMPTY_ROWS,
        columns: table.options.columns,
        getRowId: table.options.getRowId,
        getSubRows: table.options.getSubRows,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: table.options.getSubRows ? getExpandedRowModel() : undefined,
        state: {expanded: true},
        meta: table.options.meta
    });

    // Without a collected result set the print snapshot stays the table's current page.
    const rows = collectsRows && allRows ? allRowsTable.getRowModel().rows : table.getRowModel().rows;
    const selectedColumns = useMemo(() => {
        const byId = new Map(columns.map(column => [column.id, column]));
        return selectedColumnIds
            .map(id => byId.get(id))
            .filter((column): column is ServerTablePrintColumn<TData> => Boolean(column))
            .map(column => ({...column, width: columnWidths[column.id] ?? column.width}));
    }, [columns, selectedColumnIds, columnWidths]);
    const dateLabel = selectedDate?.toLocaleDateString(locale);
    const rowCountLabel = t('{{count}} rows', {count: rows.length});
    const subtitle = dateLabel ? `${dateLabel} • ${rowCountLabel}` : rowCountLabel;
    const documentTitle = [
        (title ?? t('Print')).replace(/\s+/g, '_'),
        selectedDate?.toLocaleDateString('en-CA')
    ].filter(Boolean).join('_');

    const hasWidthWarning = hasPrintColumnBelowMinimumWidth(
        selectedColumns,
        getPrintContentWidth(orientation)
    );
    const canPrint = selectedColumns.length > 0
        && !allRowsLoading
        && (rowsChoice.mode !== 'custom' || customValid);

    const handleSelectionChange = (ids: string[]) => {
        // Retained columns keep their print order while newly selected columns append.
        setSelectedColumnIds(current => {
            const kept = current.filter(id => ids.includes(id));
            const added = ids.filter(id => !current.includes(id));
            return [...kept, ...added];
        });
    };

    const handleReset = () => {
        clearServerTablePrintConfig(tableId);
        setOrientation(initialOrientation);
        setTextSize('standard');
        setRenderMode('raw');
        setSelectedColumnIds(defaultColumnIds);
        setColumnWidths({});
        initialColumnLayoutRef.current = {
            columnIds: [...defaultColumnIds],
            columnWidths: {}
        };
    };

    const handleColumnLayoutReset = () => {
        const initialLayout = initialColumnLayoutRef.current;
        if (!initialLayout) return;
        setSelectedColumnIds([...initialLayout.columnIds]);
        setColumnWidths({...initialLayout.columnWidths});
    };

    // Zero page margins suppress browser headers because each sheet owns its padding.
    const print = useReactToPrint({
        contentRef: documentRef,
        documentTitle,
        pageStyle: `
            @page { size: A4 ${orientation}; margin: 0; }
            body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .ServerTablePrintPages { gap: 0 !important; }
            .ServerTablePrintPage { box-shadow: none !important; }
        `
    });

    const handlePrint = () => {
        writeServerTablePrintConfig(tableId, {
            orientation,
            textSize,
            renderMode,
            columnIds: selectedColumnIds,
            columnWidths
        });
        print();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth fullScreen={fullScreen}>
            <DialogTitle sx={{px: {xs: 2, sm: 3}, pt: {xs: 2, sm: 2.5}, pb: 1}}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                    {t('Print Preview')}
                    <IconButton size="small" aria-label={t('Close')} onClick={onClose}>
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{px: {xs: 1.5, sm: 3}, pb: 2}}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {xs: '1fr', lg: '360px minmax(0, 1fr)'},
                        gap: 2,
                        alignItems: 'stretch'
                    }}
                >
                    <Stack sx={{pt: 1, gap: 2, minHeight: {lg: '68vh'}, minWidth: 0}}>
                        {!!printRows && (
                            <ServerTableRowsPicker
                                value={rowsChoice}
                                onChange={setRowsChoice}
                                pageRowCount={pageRowCount}
                                totalRowCount={totalRowCount}
                                max={printRows.maxRows}
                                error={rowsChoice.mode === 'custom' && !customValid ? t('Enter a number of rows.') : undefined}
                                caption={totalRowCount > printRows.maxRows
                                    ? t('Print up to {{cap, number}} rows. For more, use Export.', {cap: printRows.maxRows})
                                    : undefined}
                            />
                        )}

                        {collectsRows && !allRowsLoading && allRowsFailed && (
                            <Alert severity="warning" variant="outlined">
                                {t('The full list could not be loaded, so the print falls back to the current table page.')}
                            </Alert>
                        )}

                        {collectsRows && rowsChoice.mode === 'all' && !allRowsLoading && !!allRows && !allRows.truncated && (
                            <Alert severity="info" variant="outlined">
                                {t('The print includes every row matching the current search and filters.')}
                            </Alert>
                        )}

                        {/* A grouped table's server total counts groups, not printed rows, so it cannot be set against `n`. */}
                        {collectsRows && !allRowsLoading && !!allRows?.truncated && (
                            <Alert severity="warning" variant="outlined">
                                {table.options.getSubRows
                                    ? t('Only the first {{n}} rows are included. Narrow the filters to print the rest.', {
                                        n: allRows.printedRows
                                    })
                                    : t('Only the first {{n}} of {{total}} rows are included.', {
                                        n: allRows.rows.length,
                                        total: allRows.total
                                    })}
                            </Alert>
                        )}

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                gap: 1.5
                            }}
                        >
                            <FormControl size="small" fullWidth>
                                <InputLabel>{t('Orientation')}</InputLabel>
                                <Select
                                    label={t('Orientation')}
                                    value={orientation}
                                    onChange={event => setOrientation(event.target.value as ServerTablePrintOrientation)}
                                >
                                    <MenuItem value="landscape">{t('A4 Landscape')}</MenuItem>
                                    <MenuItem value="portrait">{t('A4 Portrait')}</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl size="small" fullWidth>
                                <InputLabel>{t('Text size')}</InputLabel>
                                <Select
                                    label={t('Text size')}
                                    value={textSize}
                                    onChange={event => setTextSize(event.target.value as ServerTablePrintTextSize)}
                                >
                                    <MenuItem value="compact">{t('Compact')}</MenuItem>
                                    <MenuItem value="standard">{t('Standard')}</MenuItem>
                                    <MenuItem value="large">{t('Large')}</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl size="small" fullWidth>
                                <InputLabel>{t('Print style')}</InputLabel>
                                <Select
                                    label={t('Print style')}
                                    value={renderMode}
                                    onChange={event => setRenderMode(event.target.value as ServerTablePrintRenderMode)}
                                >
                                    <MenuItem value="raw">{t('Raw')}</MenuItem>
                                    <MenuItem value="original">{t('Original')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        <ServerTablePrintColumnPicker
                            columns={columns}
                            selectedColumnIds={selectedColumnIds}
                            onChange={handleSelectionChange}
                        />

                        {hasWidthWarning && (
                            <Alert severity="warning" variant="outlined">
                                {t('The selected columns may be too wide for the current paper setup. Use landscape or fewer columns for a more readable printout.')}
                            </Alert>
                        )}

                        {oversizedRowCount > 0 && (
                            <Alert severity="warning" variant="outlined">
                                {t('{{count}} long rows may be clipped when printed.', {count: oversizedRowCount})}
                            </Alert>
                        )}

                        {selectedColumns.length === 0 && (
                            <Alert severity="warning" variant="outlined">
                                {t('Select at least one print column.')}
                            </Alert>
                        )}
                    </Stack>

                    <Stack sx={{gap: 0.5, minWidth: 0}}>
                        {allRowsLoading && (
                            <Alert severity="info" variant="outlined" icon={<CircularProgress size={18}/>}>
                                {rowsChoice.mode === 'all'
                                    ? t('Loading every row matching the current search and filters...')
                                    : t('Loading {{count, number}} rows...', {count: requestLimit ?? 0})}
                            </Alert>
                        )}

                        {selectedColumns.length > 0 && (
                            <Box>
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{minHeight: 28, mb: 0.25, gap: 1}}
                                >
                                    <Typography variant="caption" sx={{color: 'text.secondary'}}>
                                        {t('Drag columns to reorder, drag edges to resize.')}
                                    </Typography>
                                    <Tooltip title={t('Reset column layout')}>
                                        <IconButton
                                            size="small"
                                            aria-label={t('Reset column layout')}
                                            onClick={handleColumnLayoutReset}
                                            sx={{p: 0.375, flexShrink: 0}}
                                        >
                                            <RestartAltIcon sx={{fontSize: 18}}/>
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                                <ServerTablePrintColumnRibbon
                                    columns={selectedColumns}
                                    minWidthFraction={MIN_PRINT_COLUMN_WIDTH / getPrintContentWidth(orientation)}
                                    onOrderChange={setSelectedColumnIds}
                                    onWidthsChange={widths => setColumnWidths(current => ({...current, ...widths}))}
                                />
                                <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{mt: 0.25}}>
                                    {pageCount !== null && (
                                        <Typography variant="caption" sx={{color: 'text.secondary', whiteSpace: 'nowrap'}}>
                                            {t('{{count}} pages', {count: pageCount})}
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                        )}

                        <ServerTablePrintPreview
                            documentRef={documentRef}
                            title={title}
                            subtitle={subtitle}
                            orientation={orientation}
                            fontSize={PRINT_TEXT_SIZES[textSize]}
                            columns={selectedColumns}
                            rows={rows}
                            locale={locale}
                            renderMode={renderMode}
                            emptyText={t('No rows to print')}
                            onPageCountChange={setPageCount}
                            onOversizedRowCountChange={setOversizedRowCount}
                        />
                    </Stack>
                </Box>
            </DialogContent>

            <DialogActions sx={{px: {xs: 1.5, sm: 3}, pb: 2, pt: 1}}>
                <Button
                    color="inherit"
                    startIcon={<RestartAltIcon/>}
                    onClick={handleReset}
                    sx={{mr: 'auto'}}
                >
                    {t('Reset to defaults')}
                </Button>
                <Button onClick={onClose}>{t('Cancel')}</Button>
                <Button
                    variant="contained"
                    startIcon={<PrintIcon/>}
                    disabled={!canPrint}
                    onClick={handlePrint}
                >
                    {t('Print')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
